import React from "react";
import { cityBlocks } from "../lib/mockData";

/**
 * DarkMapMockup — Stylized SVG dark-mode city map (no external maps).
 * Used for:
 *  - Destination picker (client) — variant="destination", shows single gold pin
 *  - Optimized route (driver) — variant="route", shows path + numbered pins
 */
export const DarkMapMockup = ({
  variant = "destination",
  pin = { x: 400, y: 240 },
  onPinDrag,
  path,
  pins = [],
  className = "",
  compact = false,
}) => {
  const height = compact ? 380 : 500;

  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl border border-[#2E2F33] bg-[#0A0B0D] ${className}`}
      data-testid="dark-map-mockup"
    >
      <svg viewBox="0 0 800 500" preserveAspectRatio="xMidYMid slice" className="w-full block" style={{ height }}>
        {/* Background gradient */}
        <defs>
          <radialGradient id="mapBg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#12141A" />
            <stop offset="100%" stopColor="#07080A" />
          </radialGradient>
          <linearGradient id="goldGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#C9A227" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.5" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect x="0" y="0" width="800" height="500" fill="url(#mapBg)" />

        {/* City blocks (buildings) */}
        {cityBlocks.map((b) => (
          <rect
            key={b.id}
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            fill="#151719"
            stroke="#1E2024"
            strokeWidth="1"
            rx="2"
          />
        ))}

        {/* Main roads (horizontal + vertical) */}
        {[80, 180, 260, 340, 420].map((y) => (
          <line key={`h-${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#22242A" strokeWidth="8" />
        ))}
        {[100, 260, 420, 580, 720].map((x) => (
          <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="500" stroke="#22242A" strokeWidth="8" />
        ))}
        {[80, 180, 260, 340, 420].map((y) => (
          <line key={`hl-${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#2A2C32" strokeWidth="1" strokeDasharray="6 8" />
        ))}
        {[100, 260, 420, 580, 720].map((x) => (
          <line key={`vl-${x}`} x1={x} y1="0" x2={x} y2="500" stroke="#2A2C32" strokeWidth="1" strokeDasharray="6 8" />
        ))}

        {/* River / coast line */}
        <path
          d="M0,470 C120,450 220,490 340,460 C460,430 560,470 700,440 C740,432 780,436 800,432 L800,500 L0,500 Z"
          fill="#0E1114"
          stroke="#1B2126"
          strokeWidth="1"
        />

        {/* Labels */}
        <text x="30" y="30" fill="#3B3D42" fontSize="10" fontFamily="Fira Sans" letterSpacing="2">MIRAFLORES</text>
        <text x="330" y="105" fill="#3B3D42" fontSize="10" fontFamily="Fira Sans" letterSpacing="2">SAN ISIDRO</text>
        <text x="580" y="230" fill="#3B3D42" fontSize="10" fontFamily="Fira Sans" letterSpacing="2">BARRANCO</text>
        <text x="180" y="450" fill="#3B3D42" fontSize="10" fontFamily="Fira Sans" letterSpacing="2">OCÉANO PACÍFICO</text>

        {/* Compass */}
        <g transform="translate(750, 40)">
          <circle r="16" fill="#0D0E10" stroke="#2E2F33" strokeWidth="1" />
          <path d="M0,-10 L4,0 L0,3 L-4,0 Z" fill="#C9A227" />
          <text x="0" y="24" textAnchor="middle" fill="#A0A0A5" fontSize="8" fontFamily="Fira Sans">N</text>
        </g>

        {variant === "route" && path && (
          <>
            {/* Route polyline */}
            <polyline
              points={path}
              fill="none"
              stroke="url(#goldGrad)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
              strokeDasharray="0"
            />
            <polyline
              points={path}
              fill="none"
              stroke="#C9A227"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Numbered pins */}
            {pins.map((p, i) => (
              <g key={i} transform={`translate(${p.x}, ${p.y})`}>
                <circle r="18" fill="#0D0E10" stroke="#C9A227" strokeWidth="2" filter="url(#glow)" />
                <circle r="14" fill="#C9A227" />
                <text
                  x="0"
                  y="4"
                  textAnchor="middle"
                  fill="#0D0E10"
                  fontSize="12"
                  fontFamily="Sora"
                  fontWeight="700"
                >
                  {p.label}
                </text>
              </g>
            ))}
          </>
        )}

        {variant === "destination" && (
          <g transform={`translate(${pin.x}, ${pin.y})`} style={{ cursor: "grab" }}>
            <circle r="34" fill="#C9A227" fillOpacity="0.15" />
            <circle r="22" fill="#C9A227" fillOpacity="0.35" />
            <path
              d="M0,-24 C10,-24 18,-16 18,-6 C18,6 0,22 0,22 C0,22 -18,6 -18,-6 C-18,-16 -10,-24 0,-24 Z"
              fill="#C9A227"
              stroke="#0D0E10"
              strokeWidth="2"
              filter="url(#glow)"
            />
            <circle r="4" fill="#0D0E10" cy="-8" />
          </g>
        )}
      </svg>

      {/* Overlay grid vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,#0D0E10_100%)]" />
    </div>
  );
};

export default DarkMapMockup;
